import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Users, ChevronLeft, Calendar, Send, Clock,
  Loader2, BookOpen, MessageSquare, Video, CheckCircle
} from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';

interface GroupData {
  id: string;
  name: string;
  description: string;
  creator_name: string;
  creator_email: string;
  course_title: string;
  course_slug: string;
  track_name: string;
  max_members: number;
  is_public: boolean;
  meeting_schedule: string | null;
  timezone: string;
  member_count: number;
  next_meeting: Meeting | null;
}

interface Member {
  user_id: string;
  display_name: string;
  email: string;
  role: string;
  joined_at: string;
}

interface Meeting {
  id: string;
  title: string;
  description: string | null;
  scheduled_at: string;
  duration_minutes: number;
  meeting_url: string | null;
  agenda: string | null;
  attending_count: number;
}

interface Message {
  id: string;
  content: string;
  user_id: string;
  author_name: string;
  created_at: string;
}

export default function StudyGroup() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { fetchApi } = useApi();
  const [group, setGroup] = useState<GroupData | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [activeTab, setActiveTab] = useState<'chat' | 'meetings' | 'members'>('chat');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadGroup();
  }, [id]);

  useEffect(() => {
    if (group && user) {
      checkMembership();
    }
  }, [group, user]);

  useEffect(() => {
    if (isEnrolled) {
      loadMembers();
      loadMeetings();
      loadMessages();
    }
  }, [isEnrolled]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function loadGroup() {
    try {
      setLoading(true);
      const data = await fetchApi<{ group: GroupData }>(`/api/groups/${id}`);
      setGroup(data.group);
    } catch (err) {
      setError('Failed to load study group');
    } finally {
      setLoading(false);
    }
  }

  async function checkMembership() {
    try {
      const data = await fetchApi<{ groups: { id: string }[] }>('/api/groups/my/groups');
      setIsEnrolled(data.groups.some(g => g.id === id));
    } catch {
      // Not logged in
    }
  }

  async function loadMembers() {
    try {
      const data = await fetchApi<{ members: Member[] }>(`/api/groups/${id}/members`);
      setMembers(data.members);
    } catch {
      // May not have access
    }
  }

  async function loadMeetings() {
    try {
      const data = await fetchApi<{ meetings: Meeting[] }>(`/api/groups/${id}/meetings?upcoming=true`);
      setMeetings(data.meetings);
    } catch {
      // May not have access
    }
  }

  async function loadMessages() {
    try {
      const data = await fetchApi<{ messages: Message[] }>(`/api/groups/${id}/messages`);
      setMessages(data.messages);
    } catch {
      // May not have access
    }
  }

  async function handleJoin() {
    if (!user) {
      window.location.href = '/login';
      return;
    }

    setJoining(true);
    setError(null);

    try {
      const result = await fetchApi<{ success?: boolean; pending?: boolean }>(`/api/groups/${id}/join`, { method: 'POST' });
      if (result.pending) {
        setError('Join request submitted. Waiting for approval.');
      } else {
        setIsEnrolled(true);
        if (group) {
          setGroup({ ...group, member_count: group.member_count + 1 });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join group');
    } finally {
      setJoining(false);
    }
  }

  async function handleLeave() {
    setJoining(true);
    try {
      await fetchApi(`/api/groups/${id}/leave`, { method: 'POST' });
      setIsEnrolled(false);
      if (group) {
        setGroup({ ...group, member_count: group.member_count - 1 });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to leave group');
    } finally {
      setJoining(false);
    }
  }

  async function handleSendMessage(e: React.FormEvent) {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await fetchApi(`/api/groups/${id}/messages`, {
        method: 'POST',
        body: JSON.stringify({ content: newMessage })
      });
      setNewMessage('');
      loadMessages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  }

  async function handleRsvp(meetingId: string, status: string) {
    try {
      await fetchApi(`/api/groups/meetings/${meetingId}/rsvp`, {
        method: 'POST',
        body: JSON.stringify({ status })
      });
      loadMeetings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to RSVP');
    }
  }

  function formatDateTime(dateString: string) {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  if (error && !group) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
          <p className="text-red-400">{error}</p>
          <Link to="/community" className="text-accent-blue hover:underline mt-2 inline-block">
            ← Back to Community
          </Link>
        </div>
      </div>
    );
  }

  const isFull = Boolean(group && group.member_count >= group.max_members);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Link
        to="/community"
        className="inline-flex items-center gap-1 text-gray-400 hover:text-white mb-6"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Community
      </Link>

      {/* Header */}
      <div className="bg-gray-800/50 rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-white">{group?.name}</h1>
            {group?.course_title && (
              <Link
                to={`/courses/${group.course_slug}`}
                className="text-sm text-accent-blue hover:underline flex items-center gap-1 mt-1"
              >
                <BookOpen className="w-4 h-4" />
                {group.course_title}
              </Link>
            )}
          </div>

          {!isEnrolled ? (
            <button
              onClick={handleJoin}
              disabled={joining || isFull}
              className={`
                px-6 py-2 rounded-lg font-medium transition-colors
                ${isFull
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-accent-blue hover:bg-blue-500 text-slate-900'
                }
              `}
            >
              {joining ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isFull ? (
                'Group Full'
              ) : group?.is_public ? (
                'Join Group'
              ) : (
                'Request to Join'
              )}
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 text-green-400 text-sm">
                <CheckCircle className="w-4 h-4" />
                Member
              </span>
              <button
                onClick={handleLeave}
                disabled={joining}
                className="text-sm text-gray-400 hover:text-red-400 transition-colors"
              >
                Leave
              </button>
            </div>
          )}
        </div>

        <p className="text-gray-300 mb-4">{group?.description}</p>

        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            {group?.member_count}/{group?.max_members} members
          </span>
          {group?.meeting_schedule && (
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {group.meeting_schedule}
            </span>
          )}
        </div>

        {error && (
          <p className="mt-4 text-yellow-400 text-sm">{error}</p>
        )}
      </div>

      {/* Member Content */}
      {isEnrolled ? (
        <>
          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-gray-700">
            {(['chat', 'meetings', 'members'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`
                  px-4 py-2 font-medium transition-colors border-b-2 -mb-px capitalize
                  ${activeTab === tab
                    ? 'text-accent-blue border-accent-blue'
                    : 'text-gray-400 border-transparent hover:text-white'
                  }
                `}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Chat Tab */}
          {activeTab === 'chat' && (
            <div className="bg-gray-800/50 rounded-lg">
              <div className="h-96 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center text-gray-400 py-12">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.user_id === user?.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`
                        max-w-[70%] rounded-lg px-4 py-2
                        ${msg.user_id === user?.id
                          ? 'bg-accent-blue text-slate-900'
                          : 'bg-gray-700 text-gray-200'
                        }
                      `}>
                        {msg.user_id !== user?.id && (
                          <p className="text-xs text-gray-400 mb-1">{msg.author_name}</p>
                        )}
                        <p>{msg.content}</p>
                        <p className="text-xs opacity-60 mt-1">
                          {new Date(msg.created_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={handleSendMessage} className="border-t border-gray-700 p-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-accent-blue focus:outline-none"
                  />
                  <button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="px-4 py-2 bg-accent-blue hover:bg-blue-500 disabled:bg-gray-600 text-slate-900 rounded-lg transition-colors"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Meetings Tab */}
          {activeTab === 'meetings' && (
            <div className="space-y-4">
              {meetings.length === 0 ? (
                <div className="bg-gray-800/50 rounded-lg p-8 text-center text-gray-400">
                  <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No upcoming meetings scheduled.</p>
                </div>
              ) : (
                meetings.map(meeting => (
                  <div key={meeting.id} className="bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-white font-medium mb-1">{meeting.title}</h3>
                        {meeting.description && (
                          <p className="text-sm text-gray-400 mb-2">{meeting.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDateTime(meeting.scheduled_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {meeting.duration_minutes} min
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {meeting.attending_count} attending
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {meeting.meeting_url && (
                          <a
                            href={meeting.meeting_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-1 bg-accent-blue text-slate-900 text-sm rounded hover:bg-blue-500 transition-colors"
                          >
                            <Video className="w-4 h-4" />
                            Join
                          </a>
                        )}
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleRsvp(meeting.id, 'attending')}
                            className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded hover:bg-green-500/30"
                          >
                            Going
                          </button>
                          <button
                            onClick={() => handleRsvp(meeting.id, 'maybe')}
                            className="px-2 py-1 text-xs bg-yellow-500/20 text-yellow-400 rounded hover:bg-yellow-500/30"
                          >
                            Maybe
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Members Tab */}
          {activeTab === 'members' && (
            <div className="bg-gray-800/50 rounded-lg p-4">
              <div className="space-y-3">
                {members.map(member => (
                  <div key={member.user_id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-gray-400">
                        {member.display_name.charAt(0)}
                      </div>
                      <div>
                        <span className="text-white">{member.display_name}</span>
                        {member.role === 'admin' && (
                          <span className="ml-2 text-xs px-2 py-0.5 bg-accent-blue/20 text-accent-blue rounded">
                            Admin
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-sm text-gray-500">
                      Joined {new Date(member.joined_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-gray-800/50 rounded-lg p-8 text-center">
          <Users className="w-12 h-12 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-400">Join this study group to access chat, meetings, and member list.</p>
        </div>
      )}
    </div>
  );
}
